import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import {
  ColorOptionLabel,
  MiContentDrawer,
  MiDetailFields,
} from "@miplatform/components";
import {
  ASSIGNMENT_TEMPLATE_TYPES,
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { mdiClipboardOutline } from "@mdi/js";

const initialData = {
  name: "",
  geoFenceDistance: 0,
  shortName: "",
  description: "",
  qualificationIds: [],
  isUserPhotoRequired: false,
  color: "",
  type: "",
  AssignmentTemplateIdsToBeReviewed: [],
  autoAssignmentQualificationId: null,
  scheduleTimeThreshold: null,
};

export const AddNewAssignmentTemplateDrawer = ({
  onClose,
  onSubmit,
  loading,
  open,
  assignmentTemplateOptions,
  resourceGroups,
  roleOptions,
}) => {
  const { classes } = useStyles();
  const templateFields = [
    {
      label: "Template Name",
      name: "name",
      width: "100%",
      validationRule: z
        .string()
        .min(1, "Assignment Template name is required."),
      required: true,
    },
    {
      label: "Short Name",
      name: "shortName",
      width: "100%",
      required: true,
      validationRule: z.string().min(1, "Assignment short name is required."),
    },
    {
      label: "Type",
      name: "type",
      fieldType: INPUT_TYPES.SELECT,
      options: Object.values(ASSIGNMENT_TEMPLATE_TYPES).map((item) => ({
        label: item,
        value: item,
      })),
      validationRule: z
        .string()
        .min(1, "Assignment Template type is required."),
      required: true,
    },
    {
      label: "Geofence Distance",
      name: "geoFenceDistance",
      width: "100%",
      fieldType: INPUT_TYPES.NUMBER,
    },
    {
      label: "Schedule Time Threshold (hours)",
      name: "scheduleTimeThreshold",
      width: "100%",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z
        .number()
        .min(0.0, "Schedule Time Threshold cannot be negative")
        .nullable(),
    },
    {
      label: "Color",
      name: "color",
      fieldType: INPUT_TYPES.SELECT,
      options: CONFIGURABLE_COLORS.map(({ color, label }) => ({
        label: <ColorOptionLabel color={color} label={label} />,
        value: color,
      })),
      fullWidth: true,
      width: "100%",
      validationRule: z.string().min(1, "Color is required"),
    },
    {
      label: "Description",
      name: "description",
      fieldType: INPUT_TYPES.TEXTAREA,
      width: "100%",
      validationRule: z.string().min(1, "Description is required"),
      required: true,
    },
    {
      label: "Assignable Resource Groups",
      name: "qualificationIds",
      options: resourceGroups,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      width: "100%",
      minWidth: "180px",
      required: true,
      validationRule: z.array(z.string().optional()).optional(),
    },
    {
      label: "Auto Assign Resource Groups",
      name: "autoAssignmentQualificationId",
      width: "50%",
      minWidth: "180px",
      fieldType: INPUT_TYPES.SELECT,
    },
    {
      label: "Roles Allowed to Assign",
      name: "roleIdsAllowedToAssign",
      options: roleOptions,
      fieldType: INPUT_TYPES.MULTI_SELECT,
      width: "100%",
      minWidth: "180px",
      required: true,
      validationRule: z.array(z.string().optional()).optional(),
    },
    {
      label: "Is user photo required",
      name: "isUserPhotoRequired",
      width: "100%",
      flexGrow: 0,
      fieldType: INPUT_TYPES.SWITCH,
    },
  ];
  const edit = useEdit(initialData, templateFields);
  let updatedTemplateFields =
    edit.getValue("type") === ASSIGNMENT_TEMPLATE_TYPES.REVIEW
      ? [
          ...templateFields,
          {
            label: "Reviewing Assignment Template",
            fieldType: INPUT_TYPES.MULTI_SELECT,
            name: "AssignmentTemplateIdsToBeReviewed",
            options: assignmentTemplateOptions,
          },
        ]
      : templateFields;

  updatedTemplateFields = updatedTemplateFields.map((item) => {
    if (
      item.name !== "autoAssignmentQualificationId" &&
      item.name !== "qualificationIds"
    )
      return item;

    if (item.name === "qualificationIds") {
      item.afterOnChange = (value) => {
        if (value.length === 0) {
          edit.update({
            autoAssignmentQualificationId: null,
            qualificationIds: [],
          });
        }
      };
    }
    if (item.name === "autoAssignmentQualificationId") {
      item.options = edit.getValue("qualificationIds")?.map((id) => {
        const resourceGroup = resourceGroups.find(({ value }) => value === id);
        return {
          label: resourceGroup?.label,
          value: resourceGroup?.value,
        };
      });
    }
    return item;
  });

  const handleClose = () => {
    onClose();
    edit.reset();
  };

  return (
    <MiContentDrawer
      open={open}
      variant="temporary"
      leftIcon={mdiClipboardOutline}
      onClose={handleClose}
      actions={[
        {
          name: "Cancel",
          variant: BUTTON_VARIANTS.TEXT,
          size: "medium",
          color: BUTTON_COLORS.BLACK,
          onClick: handleClose,
        },
        {
          name: "Submit",
          variant: BUTTON_VARIANTS.CONTAINED,
          size: "medium",
          color: BUTTON_COLORS.PRIMARY,
          onClick: () => onSubmit(edit.edits),
          disabled: !edit.isDirty() || !edit.isValid(),
        },
      ]}
      isLoading={loading}
      title="Add Assignment Template"
      topRightClose={edit.reset}
    >
      <MiDetailFields
        detailFields={updatedTemplateFields}
        edit={edit}
        containerStyle={classes.container}
      />
    </MiContentDrawer>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(3),
    width: "100%",
  },
}));
